import { useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import { faLock, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import { useAnswerGame } from '../../hooks/useGame';
import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import ChevronIcon from '../icons/Chevron';
import QuestionStatus from '../partials/QuestionStatus';
import SportLivePeriod from '../partials/SportLivePeriod';

import styles from '../../../styles/games/zone.module.scss';

const propTypes = {
    className: PropTypes.string,
    game: AppPropTypes.game,
    participation: AppPropTypes.participation,
    canUpdateAnswer: PropTypes.bool,
};

const defaultProps = {
    className: null,
    game: null,
    participation: null,
    canUpdateAnswer: false,
};

function ZoneGame({ className, game, participation, canUpdateAnswer }) {
    const route = useUrlGenerator();
    const tracking = useTracking();

    const themeClassName = useThemeClassName(styles);

    const { id: gameId, questions, sportGames } = game || {};

    const { endedAt: participationEndedAt = null, answers = [], points } = participation || {};

    const ended =
        participationEndedAt !== null &&
        differenceInSeconds(parseISO(participationEndedAt), new Date()) <= 0;

    const canPlay = !ended;

    const [currentAnswers, setCurrentAnswers] = useState(answers || []);
    const onClickChoice = useCallback(
        (questionId, choiceValue) => {
            const previousAnswer =
                (currentAnswers.find((ans) => ans.question === questionId) || null) !== null;
            if (!previousAnswer) {
                setCurrentAnswers([
                    ...currentAnswers,
                    {
                        question: questionId,
                        value: choiceValue,
                    },
                ]);
            } else {
                const newAnswers = currentAnswers
                    .map((answer) => {
                        if (answer.question === questionId) {
                            return {
                                ...answer,
                                question: questionId,
                                value: choiceValue !== answer.value ? choiceValue : null,
                            };
                        }
                        return answer;
                    })
                    .filter((answer) => answer.value !== null);
                setCurrentAnswers(newAnswers);
            }
        },
        [currentAnswers],
    );

    const { answerGameAsync, isMutating: loading } = useAnswerGame({
        game,
        participation,
    });

    const onConfirmClick = useCallback(() => {
        answerGameAsync(currentAnswers).then(
            ({
                participation: updatedParticipation,
                participant: updatedParticipant,
                game: updatedGame = null,
            }) => {
                const { answers: newAnswers = [] } = updatedParticipation || {};
                setCurrentAnswers(newAnswers);

                tracking.trackInteraction('game_answer_question', {
                    game: updatedGame || game,
                    participant: updatedParticipant,
                    participation: updatedParticipation,
                    question: 'zone',
                    choice: 'zone',
                });
            },
        );
    }, [gameId, questions, currentAnswers, setCurrentAnswers, tracking]);

    const allAnswered = useMemo(
        () => answers !== null && questions !== null && answers.length === questions.length,
        [answers, questions],
    );

    // const allEnded = useMemo(
    //     () => answers !== null && questions !== null && answers.length === questions.length,
    //     [answers, questions],
    // );

    // const currentRoute =
    //     participationId !== null
    //         ? route('participation', { participation: participationId })
    //         : route('game', { game: gameId });

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.ended]: ended,
                    [styles.loading]: loading,
                },
                themeClassName,
                className,
            ])}
        >
            <div className={styles.wrapper}>
                <Link href={route('play')} className={styles.zone}>
                    <div className={styles.zoneInner}>
                        <div className={styles.zoneArrow}>
                            <ChevronIcon className={styles.chevron} direction="left" />
                        </div>
                        <span className={styles.zoneTitle}>
                            <FormattedMessage
                                defaultMessage="Back to classic mode"
                                description="Text label"
                            />
                        </span>
                        <div className={styles.empty} />
                    </div>
                </Link>
                <div className={styles.inner}>
                    <div className={styles.logo} />
                    {!ended ? (
                        <>
                            <h3 className={styles.title}>
                                <FormattedMessage
                                    defaultMessage="Predict them all!"
                                    description="Text label"
                                />
                            </h3>
                            <p className={styles.description}>
                                <FormattedMessage
                                    defaultMessage="Predict the outcome of every game!"
                                    description="Text label"
                                />
                            </p>
                        </>
                    ) : (
                        <>
                            <h3 className={styles.title}>
                                <FormattedMessage
                                    defaultMessage="WHAT A CHAMP! YOU SCORED:"
                                    description="Text label"
                                />
                            </h3>
                            <div className={styles.score}>
                                <div className={styles.box}>{points || 0} PTS</div>
                            </div>
                            <p className={styles.description}>
                                {points !== null && points > 0 ? (
                                    <FormattedMessage
                                        defaultMessage="You were definitely in the zone today! See you next game for more predictions."
                                        description="Text label"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="See you next game for more predictions."
                                        description="Text label"
                                    />
                                )}
                            </p>
                        </>
                    )}

                    <h3 className={classNames([styles.title, styles.largeTitle])}>
                        <FormattedMessage
                            defaultMessage="Which teams are going to win today?"
                            description="Text label"
                        />
                    </h3>
                    <div className={styles.questions}>
                        {(questions || []).map((question) => {
                            const {
                                id: questionId,
                                body = null,
                                choices = [],
                                canAnswer = true,
                                points: questionPoints = null,
                            } = question || {};

                            const parts = questionId.split('_');
                            const [, , sportId] = parts || [];

                            const sportGame =
                                (sportGames || []).find((g) => g.id === sportId) || null;

                            const {
                                isStarted = false,
                                isEnded = false,
                                teamHome = null,
                                teamAway = null,
                                // periodTime = null,
                            } = sportGame || {};

                            const {
                                abbreviation: homeAbbreviation = null,
                                score: homeScore = null,
                            } = teamHome || {};
                            const { score: awayScore = null } = teamAway || {};

                            const answer =
                                currentAnswers.find((ans) => ans.question === questionId) || null;

                            const {
                                id: answerId = null,
                                value: answerValue = null,
                                validated = false,
                                valid = false,
                            } = answer || {};

                            const questionGame =
                                (sportGames || []).find(
                                    (g) => `zone_winner_${g.id}` === questionId,
                                ) || null;
                            const { ended: questionEnded = false } = questionGame || {};

                            // console.log('sportGame', sportGame);

                            return (
                                <div key={`question-${questionId}`} className={styles.question}>
                                    {sportGame === null ? (
                                        <div className={styles.body}>
                                            <FormattedDate
                                                value={body}
                                                hour="numeric"
                                                minute="numeric"
                                            />
                                        </div>
                                    ) : (
                                        <SportLivePeriod
                                            className={styles.livePeriod}
                                            sportGame={sportGame}
                                            animationColor="rgba(255, 255, 255, 0.5)"
                                        />
                                    )}
                                    <div className={styles.choices}>
                                        {(choices || []).map((choice) => {
                                            const {
                                                id: choiceId,
                                                label,
                                                pointsMultiplier = null,
                                            } = choice || {};

                                            const disabled =
                                                !canAnswer ||
                                                !canPlay ||
                                                isEnded ||
                                                (answerId !== null && !canUpdateAnswer);

                                            const success =
                                                answerId !== null &&
                                                answerValue === choiceId &&
                                                validated &&
                                                valid;

                                            const error =
                                                answerId !== null &&
                                                answerValue === choiceId &&
                                                validated &&
                                                !valid;

                                            const showAnswer =
                                                (success || error) && answerId !== null;

                                            const answeredChoice =
                                                answerId !== null && answerValue === choiceId;

                                            const expired = isEnded && answerId === null;
                                            const isHome = homeAbbreviation === choiceId;
                                            const score = isHome ? homeScore : awayScore;

                                            return (
                                                <button
                                                    type="button"
                                                    key={`choice_${choiceId}`}
                                                    className={classNames([
                                                        styles.choice,
                                                        {
                                                            [styles.selected]:
                                                                answer !== null &&
                                                                answerValue === choiceId,
                                                            [styles.disabled]: disabled,
                                                            [styles.chosen]:
                                                                answerId !== null &&
                                                                answerValue === choiceId,
                                                            [styles.success]: success,
                                                            [styles.error]: error,
                                                            [styles.expired]: expired,
                                                        },
                                                    ])}
                                                    disabled={disabled}
                                                    onClick={() =>
                                                        onClickChoice(questionId, choiceId)
                                                    }
                                                >
                                                    <div className={styles.choiceInner}>
                                                        <div className={styles.choiceLabel}>
                                                            {label}
                                                        </div>
                                                        {pointsMultiplier !== null &&
                                                        !isStarted &&
                                                        !isEnded ? (
                                                            <div className={styles.choiceValue}>
                                                                <span className={styles.multiplyer}>
                                                                    {pointsMultiplier}{' '}
                                                                </span>
                                                                <FormattedMessage
                                                                    defaultMessage="x pts"
                                                                    description="Choice odds"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className={styles.choiceValue}>
                                                                {questionEnded ? (
                                                                    <>
                                                                        <FormattedMessage
                                                                            defaultMessage="Final score"
                                                                            description="Choice score"
                                                                        />
                                                                        <span
                                                                            className={
                                                                                styles.strong
                                                                            }
                                                                        >
                                                                            {' '}
                                                                            {score}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FormattedMessage
                                                                            defaultMessage="Team score"
                                                                            description="Choice score"
                                                                        />
                                                                        <span
                                                                            className={
                                                                                styles.strong
                                                                            }
                                                                        >
                                                                            {' '}
                                                                            {score}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                        {expired ? (
                                                            <div className={styles.choicePill}>
                                                                <div
                                                                    className={
                                                                        styles.choicePillInner
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon
                                                                        className={styles.icon}
                                                                        icon={faTimesCircle}
                                                                    />
                                                                    <FormattedMessage
                                                                        defaultMessage="Time expired"
                                                                        description="Choice result"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                        {!expired &&
                                                        answeredChoice &&
                                                        !showAnswer ? (
                                                            <div className={styles.choicePill}>
                                                                <div
                                                                    className={
                                                                        styles.choicePillInner
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon
                                                                        className={styles.icon}
                                                                        icon={faLock}
                                                                    />
                                                                    <FormattedMessage
                                                                        defaultMessage="Answered"
                                                                        description="Choice result"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                        {!expired &&
                                                        !answeredChoice &&
                                                        !showAnswer ? (
                                                            <div
                                                                className={classNames([
                                                                    styles.choicePill,
                                                                    styles.hidden,
                                                                ])}
                                                            >
                                                                <div
                                                                    className={
                                                                        styles.choicePillInner
                                                                    }
                                                                >
                                                                    -
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                        {!expired && showAnswer ? (
                                                            <div className={styles.choiceResult}>
                                                                <QuestionStatus
                                                                    className={
                                                                        styles.questionResult
                                                                    }
                                                                    question={question}
                                                                    answer={answer}
                                                                    ended={validated}
                                                                />
                                                                <span
                                                                    className={
                                                                        styles.questionResultLabel
                                                                    }
                                                                >
                                                                    {success ? (
                                                                        <span
                                                                            className={styles.pill}
                                                                        >
                                                                            <FormattedMessage
                                                                                defaultMessage="{points} pts"
                                                                                description="Choice result"
                                                                                values={{
                                                                                    points: questionPoints,
                                                                                }}
                                                                            />
                                                                        </span>
                                                                    ) : (
                                                                        <span
                                                                            className={
                                                                                styles.smallLabel
                                                                            }
                                                                        >
                                                                            <FormattedMessage
                                                                                defaultMessage="Missed"
                                                                                description="Choice result"
                                                                            />
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {points !== null && points > 0 ? (
                        <div className={styles.title}>
                            <FormattedMessage
                                defaultMessage="You have won {points, number} points!"
                                description="Text label"
                                values={{ points }}
                            />
                        </div>
                    ) : null}
                    <div className={styles.confirm}>
                        <StyledButton
                            type="button"
                            className={classNames([
                                styles.confirmButton,
                                styles.button,
                                { [styles.disabled]: allAnswered },
                            ])}
                            onClick={onConfirmClick}
                            disabled={allAnswered}
                        >
                            {allAnswered ? (
                                <FormattedMessage
                                    defaultMessage="Thank you, all your answers have been saved."
                                    description="Button label"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Confirm and lock my answers"
                                    description="Button label"
                                />
                            )}
                        </StyledButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

ZoneGame.propTypes = propTypes;
ZoneGame.defaultProps = defaultProps;

export default ZoneGame;
